import React from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Share2, Eye } from 'lucide-react';

const truncateMarkdown = (markdown, maxLength) => {
  const plainText = markdown
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_~`>#>-]/g, '')
    .replace(/\n{2,}/g, ' – ')
    .replace(/\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return plainText.length > maxLength
    ? plainText.slice(0, maxLength - 1) + '…'
    : plainText;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
};

const JobCard = ({ job }) => {
  const today = new Date();
  const closingDate = new Date(job.closingDate);
  const timeDiff = closingDate - today;
  const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

  let statusLabel = '';
  let backgroundColor = '';

  if (daysRemaining < 0) {
    statusLabel = 'Expired';
    backgroundColor = '#d32f2f';
  } else if (daysRemaining <= 3) {
    statusLabel = `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`;
    backgroundColor = '#fbc02d';
  } else {
    statusLabel = `${daysRemaining} days left`;
    backgroundColor = '#388e3c';
  }

    const formatJobTitle = (title) => {
      return title.trim().toLowerCase().replace(/\s+/g, '-');
    };

  const handleShare = (e) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/job/${job.id}#${formatJobTitle(job.title)}`;
    const message = `🚨 New Job Opportunity!

💼 *${job.title}*
🏢 *${job.companyName}*
📍 *${job.location}*
🗓️ *Deadline:* ${formatDate(job.closingDate)}

👉 View full details & apply:
${shareUrl}

✅ More active jobs available at: ${window.location.origin}

#JobOpportunity #NowHiring #CareerMove #ApplyToday`;

    if (navigator.share) {
      navigator.share({
        title: `Job Opening: ${job.title}`,
        text: message,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(message).then(() => {
        toast.success('📋 Job details copied to clipboard!', {
          position: 'top-center',
        });
      }).catch(() => {
        toast.error('❌ Failed to copy job details.', {
          position: 'top-center',
        });
      });
    }
  };

  const handleView = (e) => {
    e.stopPropagation();
    const jobUrl = `${window.location.origin}/job/${job.id}#${formatJobTitle(job.title)}`;
    window.open(jobUrl, '_self', 'noopener,noreferrer'); // opens in same tab
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <strong>{job.title}</strong>
        <span style={{ ...styles.status, backgroundColor }}>{statusLabel}</span>
      </div>
      <p><strong>Company:</strong> {job.companyName}</p>
      {job.companyDetails && (
        <div className="text-justify">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {truncateMarkdown(job.companyDetails.replace(/\\n/g, '\n'), 700)}
          </ReactMarkdown>
        </div>
      )}
      <p><strong>Deadline:</strong> {formatDate(job.closingDate)}</p>

      <div style={styles.actions}>
        <button onClick={handleShare} style={styles.share}>
          <Share2 size={16} style={{ marginRight: 6 }} />
          Share
        </button>
        <button onClick={handleView} style={styles.view}>
          <Eye size={16} style={{ marginRight: 6 }} />
          View Job
        </button>
      </div>
    </div>
  );
};

const styles = {
  card: {
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '12px',
    cursor: 'default',
    transition: 'box-shadow 0.2s ease',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  status: {
    color: '#fff',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
    minWidth: '80px',
    textAlign: 'center',
  },
  actions: {
    marginTop: '10px',
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  share: {
    backgroundColor: '#f57c00',
    color: '#fff',
    padding: '6px 12px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  view: {
    backgroundColor: '#00796b',
    color: '#fff',
    padding: '6px 12px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
};

export default JobCard;
